// crewbit-admin/src/components/money/CashFlowSparkline.tsx
//
// SPEC §Overview.7.2. Cash flow trend visualizer slot in the 2-col grid.
//
// v1 ships an empty-state placeholder — the envelope does not yet carry a
// daily series for the chart. Filed as `§CashFlowSparklineSeries` deferred
// (Session 119 §4): patch `financial_summary` to return
// `cash_flow_series: Array<{date, money_in, expenses}>` (e.g., 30 daily
// points), then swap this body for a recharts implementation.
//
// `minHeight: 200` keeps the 2-col grid layout stable so OutstandingAgingCard
// doesn't visually expand to fill the row (§10.15).
//
// No recharts import. No mock data.

import { TrendingUp } from 'lucide-react';
import { admin, palette, shadow, spacing, transition } from '../../styles/tokens';

interface CashFlowSparklineProps {
  periodLabel?: string;
  onClick?: () => void;
}

export function CashFlowSparkline({ periodLabel, onClick }: CashFlowSparklineProps) {
  const title = periodLabel ? `Cash flow · ${periodLabel}` : 'Cash flow';
  const interactive = onClick !== undefined;
  const handleActivate = () => {
    if (onClick) onClick();
  };

  const interactiveProps = interactive
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: handleActivate,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleActivate();
          }
        },
        'aria-label': 'Cash flow — open Revenue dashboard',
      }
    : {};

  return (
    <div
      {...interactiveProps}
      style={{
        background: palette.white,
        borderRadius: admin.cardRadius,
        border: `1px solid ${admin.border}`,
        padding: spacing.lg,
        boxShadow: shadow.card,
        minHeight: 200,
        display: 'flex',
        flexDirection: 'column',
        cursor: interactive ? 'pointer' : 'default',
        transition: interactive ? `box-shadow ${transition.fast}` : undefined,
      }}
      onMouseEnter={interactive ? (e) => (e.currentTarget.style.boxShadow = shadow.cardHover) : undefined}
      onMouseLeave={interactive ? (e) => (e.currentTarget.style.boxShadow = shadow.card) : undefined}
    >
      <div style={{ fontSize: 14, fontWeight: 600, color: admin.header }}>
        {title}
      </div>

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: spacing.sm,
          paddingTop: spacing.md,
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: palette.accentWash,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <TrendingUp size={18} color={palette.accent} strokeWidth={2} />
        </div>
        <div style={{ fontSize: 13.5, color: palette.g500, textAlign: 'center' }}>
          Coming soon — cash flow trend visualizer
        </div>
      </div>
    </div>
  );
}
